import React, { useState, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import {
    getPlans,
    getSubscription,
    createCheckoutSession,
    createPortalSession,
    downgradeToFree,
    getInvoices,
} from '../services/billingApi';
import { getUsage } from '../services/tierApi';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';
import { createLogger } from '../../../shared/config/logger';

const logger = createLogger('MembershipSettings');

/** Mirrors backend `tierLimits.js` — used only if API omits matching `features.*` keys (`passesExtras` = extra customization for wallet passes). */
const FALLBACK_PASSES_EXTRAS_TIERS = new Set(['starter', 'pro', 'enterprise']);
const FALLBACK_REWARD_PUNCH_AND_VISITS_TIERS = new Set(['starter', 'pro', 'enterprise']);
const FALLBACK_REWARD_BIRTHDAY_TIERS = new Set(['pro', 'enterprise']);

function passesExtrasFromTier(tier) {
    return FALLBACK_PASSES_EXTRAS_TIERS.has(String(tier || 'free').toLowerCase());
}

function rewardsPunchCardFromTier(tier) {
    return FALLBACK_REWARD_PUNCH_AND_VISITS_TIERS.has(String(tier || 'free').toLowerCase());
}

function rewardsVisitsFromTier(tier) {
    return FALLBACK_REWARD_PUNCH_AND_VISITS_TIERS.has(String(tier || 'free').toLowerCase());
}

function rewardsBirthdayFromTier(tier) {
    return FALLBACK_REWARD_BIRTHDAY_TIERS.has(String(tier || 'free').toLowerCase());
}

const DEFAULT_USAGE_FALLBACK = {
    tier: 'free',
    limits: { passes: 50, emails: 25, sms: 0, rewards: 1 },
    features: {
        passesExtras: false,
        rewardsPoints: true,
        rewardsPunchCard: false,
        rewardsVisits: false,
        rewardsBirthday: false,
    },
    usage: { passes: 0, rewards: 0, emails: 0, sms: 0 },
};

/** Open vs closed lock row for plan feature access (membership usage panel). */
function PlanAccessRow({ label, included }) {
    return (
        <div className="flex w-full min-w-0 items-center justify-between gap-4 py-2">
            <span className="text-sm font-medium text-gray-600 leading-snug min-w-0 flex-1 pr-2">
                {label}
            </span>
            {included ? (
                <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-800 border border-emerald-100">
                    <LockOpenIcon className="h-5 w-5 shrink-0" aria-hidden />
                    Included
                </span>
            ) : (
                <span className="inline-flex shrink-0 items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-200">
                    <LockClosedIcon className="h-5 w-5 shrink-0" aria-hidden />
                    Not included
                </span>
            )}
        </div>
    );
}

export default function MembershipSettings() {
    const [plans, setPlans] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingSubscription, setLoadingSubscription] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showInvoices, setShowInvoices] = useState(false);
    const [availablePlansOpen, setAvailablePlansOpen] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            setSuccessMessage(null);

            // Fetch plans, subscription, invoices, and usage in parallel
            const [plansRes, subscriptionRes, invoicesRes, usageRes] = await Promise.all([
                getPlans().catch(() => ({ data: { plans: [] } })),
                getSubscription().catch(() => ({ data: { hasSubscription: false, tier: 'free' } })),
                getInvoices().catch(() => ({ data: { invoices: [] } })),
                getUsage().catch(() => ({ data: DEFAULT_USAGE_FALLBACK })),
            ]);

            // Add free tier as an option
            const stripePlans = plansRes.data?.plans || plansRes.data || [];
            const freeTierPlan = {
                id: 'free',
                name: 'Free',
                description: 'Perfect for getting started with LoyalPass. This plan includes 50 wallet passes per month, 50 marketing emails per month, and one reward. Start building your loyalty program today—no credit card required. Ideal for small businesses testing the platform or getting started with digital loyalty programs. All core features are included at no cost.',
                prices: [{
                    id: 'free',
                    unit_amount: 0,
                    amount: 0,
                    currency: 'usd',
                    recurring: {
                        interval: 'month'
                    },
                    interval: 'month',
                    intervalCount: 1
                }]
            };
            const allPlans = [freeTierPlan, ...stripePlans];
            setPlans(allPlans);
            setSubscription(subscriptionRes.data || { hasSubscription: false, tier: 'free' });
            setInvoices(invoicesRes.data?.invoices || []);
            setUsage(usageRes.data || DEFAULT_USAGE_FALLBACK);
        } catch (err) {
            const errorMessage = extractErrorMessage(err);
            logger.error('Failed to fetch membership data', { error: errorMessage });
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSubscribe = async (priceId) => {
        try {
            setLoadingSubscription(true);
            setError(null);
            setSuccessMessage(null);
            
            if (priceId === 'free') {
                const activeTier = String(
                    subscription?.tier || usage?.tier || 'free'
                ).toLowerCase();

                if (activeTier === 'free') {
                    setError('You are already on the free tier.');
                    setLoadingSubscription(false);
                    return;
                }

                if (subscription?.hasSubscription) {
                    const response = await createPortalSession();
                    if (response.data?.url) {
                        window.location.href = response.data.url;
                    }
                    return;
                }

                const response = await downgradeToFree();
                setSuccessMessage(
                    response.data?.message || 'Your plan has been changed to the free tier.'
                );
                await fetchData();
                return;
            }
            
            const response = await createCheckoutSession(priceId);
            if (response.data?.url) {
                window.location.href = response.data.url;
            }
        } catch (err) {
            const errorMessage = extractErrorMessage(err);
            logger.error('Failed to create checkout session', { error: errorMessage });
            setError(errorMessage);
        } finally {
            setLoadingSubscription(false);
        }
    };

    const handleManageSubscription = async () => {
        try {
            setLoadingSubscription(true);
            setError(null);
            const response = await createPortalSession();
            if (response.data?.url) {
                window.location.href = response.data.url;
            }
        } catch (err) {
            const errorMessage = extractErrorMessage(err);
            logger.error('Failed to create portal session', { error: errorMessage });
            setError(errorMessage);
        } finally {
            setLoadingSubscription(false);
        }
    };

    const formatPrice = (amount, currency = 'usd') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(amount / 100);
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp * 1000).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const getTierDisplayName = (tier) => {
        return tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : 'Free';
    };

    if (loading) {
        return (
            <div className="p-8 max-w-6xl mx-auto">
                <h2 className="text-3xl font-bold text-primary mb-6">Membership Settings</h2>
                <div className="bg-white shadow-md rounded-xl p-8">
                    <div className="text-center text-gray-600">Loading membership information...</div>
                </div>
            </div>
        );
    }

    const currentTier = subscription?.tier || 'free';
    const hasSubscription = subscription?.hasSubscription || false;

    const passesExtrasAccess =
        usage &&
        (typeof usage.features?.passesExtras === 'boolean'
            ? usage.features.passesExtras
            : passesExtrasFromTier(usage.tier || currentTier));

    const tierForFeatures = String(usage?.tier || currentTier || 'free').toLowerCase();

    const pointsRewardAccess =
        usage &&
        (typeof usage.features?.rewardsPoints === 'boolean' ? usage.features.rewardsPoints : true);

    const punchCardRewardAccess =
        usage &&
        (typeof usage.features?.rewardsPunchCard === 'boolean'
            ? usage.features.rewardsPunchCard
            : rewardsPunchCardFromTier(tierForFeatures));

    const visitsRewardAccess =
        usage &&
        (typeof usage.features?.rewardsVisits === 'boolean'
            ? usage.features.rewardsVisits
            : rewardsVisitsFromTier(tierForFeatures));

    const birthdayRewardAccess =
        usage &&
        (typeof usage.features?.rewardsBirthday === 'boolean'
            ? usage.features.rewardsBirthday
            : rewardsBirthdayFromTier(tierForFeatures));

    return (
        <div className="p-8 max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-primary mb-6">Membership Settings</h2>

            {successMessage && (
                <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded">
                    {successMessage}
                </div>
            )}

            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                    {error}
                </div>
            )}

            {/* Current Plan Section */}
            <div className="bg-white shadow-md rounded-xl p-6 mb-6">
                <h3 className="text-xl font-semibold mb-4">Current Plan</h3>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <p className="text-2xl font-bold text-primary">
                            {getTierDisplayName(currentTier)} Tier
                        </p>
                        {hasSubscription && subscription?.product && (
                            <p className="text-gray-600 mt-1">{subscription.product.name}</p>
                        )}
                        {hasSubscription && subscription?.price && (
                            <p className="text-gray-600">
                                {formatPrice(subscription.price.amount, subscription.price.currency)}/
                                {subscription.price.interval}
                            </p>
                        )}
                    </div>
                    <div className="flex gap-3">
                        {hasSubscription && (
                            <button
                                onClick={handleManageSubscription}
                                disabled={loadingSubscription}
                                className="bg-primary text-white px-6 py-2 rounded hover:bg-primary-dark transition disabled:opacity-50"
                            >
                                {loadingSubscription ? 'Loading...' : 'Manage Subscription'}
                            </button>
                        )}
                        {invoices.length > 0 && (
                            <button
                                onClick={() => setShowInvoices(!showInvoices)}
                                className="bg-gray-200 text-gray-800 px-6 py-2 rounded hover:bg-gray-300 transition"
                            >
                                {showInvoices ? 'Hide Invoices' : 'View Invoices'}
                            </button>
                        )}
                    </div>
                </div>

                {hasSubscription && (
                    <div className="mt-4 pt-4 border-t">
                        <div className="grid grid-cols-2 gap-4">
                            {subscription?.currentPeriodEnd && (
                                <div>
                                    <p className="text-sm text-gray-600">Next Billing Date</p>
                                    <p className="font-semibold">{formatDate(subscription.currentPeriodEnd)}</p>
                                </div>
                            )}
                            {subscription?.paymentMethod?.card && (
                                <div>
                                    <p className="text-sm text-gray-600">Payment Method</p>
                                    <p className="font-semibold">
                                        {subscription.paymentMethod.card.brand.toUpperCase()} ••••{' '}
                                        {subscription.paymentMethod.card.last4}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Usage Section */}
            {usage && usage.usage && (
                <div className="bg-white shadow-md rounded-xl p-6 mb-6">
                    <h3 className="text-xl font-semibold mb-4">Usage</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <p className="text-sm text-gray-600">Passes Created</p>
                            <p className="text-2xl font-bold text-primary">
                                {usage.usage.passes || 0}
                                {usage.limits?.passes !== null && usage.limits?.passes !== undefined && (
                                    <span className="text-lg font-normal text-gray-500"> / {usage.limits.passes}</span>
                                )}
                                {usage.limits?.passes === null && (
                                    <span className="text-lg font-normal text-gray-500"> / ∞</span>
                                )}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Rewards Active</p>
                            <p className="text-2xl font-bold text-primary">
                                {usage.usage.rewards || 0}
                                {usage.limits?.rewards !== null && usage.limits?.rewards !== undefined && (
                                    <span className="text-lg font-normal text-gray-500"> / {usage.limits.rewards}</span>
                                )}
                                {usage.limits?.rewards === null && (
                                    <span className="text-lg font-normal text-gray-500"> / ∞</span>
                                )}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Emails Sent (This Month)</p>
                            <p className="text-2xl font-bold text-primary">
                                {usage.usage.emails || 0}
                                {usage.limits?.emails !== null && usage.limits?.emails !== undefined && (
                                    <span className="text-lg font-normal text-gray-500"> / {usage.limits.emails}</span>
                                )}
                                {usage.limits?.emails === null && (
                                    <span className="text-lg font-normal text-gray-500"> / ∞</span>
                                )}
                            </p>
                        </div>
                        {usage.limits?.sms !== null && usage.limits?.sms > 0 && (
                            <div>
                                <p className="text-sm text-gray-600">SMS Sent (This Month)</p>
                                <p className="text-2xl font-bold text-primary">
                                    {usage.usage.sms || 0}
                                    {usage.limits?.sms !== null && (
                                        <span className="text-lg font-normal text-gray-500"> / {usage.limits.sms}</span>
                                    )}
                                </p>
                            </div>
                        )}
                    </div>
                    <div className="mt-5 pt-4 border-t border-gray-100">
                        <p className="text-sm font-semibold text-gray-800 mb-3">Plan Access</p>
                        <PlanAccessRow label="Extra Customization for Wallet Passes" included={!!passesExtrasAccess} />
                        <PlanAccessRow label="Points" included={!!pointsRewardAccess} />
                        <PlanAccessRow label="Punch cards" included={!!punchCardRewardAccess} />
                        <PlanAccessRow label="Visits" included={!!visitsRewardAccess} />
                        <PlanAccessRow label="Birthday" included={!!birthdayRewardAccess} />
                    </div>
                </div>
            )}

            {/* Available Plans — accordion */}
            {plans.length > 0 && (
                <div className="bg-white shadow-md rounded-xl mb-6 overflow-hidden">
                    <button
                        type="button"
                        className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left hover:bg-gray-50/80 transition"
                        onClick={() => setAvailablePlansOpen((open) => !open)}
                        aria-expanded={availablePlansOpen}
                        aria-controls="membership-available-plans-panel"
                        id="membership-available-plans-trigger"
                    >
                        <h3 className="text-xl font-semibold">Available Plans</h3>
                        <ChevronDownIcon
                            className={`h-5 w-5 shrink-0 text-gray-500 transition-transform duration-200 ${
                                availablePlansOpen ? 'rotate-180' : ''
                            }`}
                            aria-hidden
                        />
                    </button>
                    {availablePlansOpen && (
                        <div
                            id="membership-available-plans-panel"
                            role="region"
                            aria-labelledby="membership-available-plans-trigger"
                            className="px-6 pb-6 pt-0 border-t border-gray-100"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
                                {plans
                                    .filter((plan) => !(plan.id === 'free' && currentTier === 'free'))
                                    .map((plan) => (
                                        <div
                                            key={plan.id}
                                            className={`border-2 rounded-lg p-6 ${
                                                (subscription?.priceId &&
                                                    plan.prices.some((p) => p.id === subscription.priceId)) ||
                                                (!hasSubscription && plan.id === 'free' && currentTier === 'free')
                                                    ? 'border-primary bg-primary/5'
                                                    : 'border-gray-200'
                                            }`}
                                        >
                                            <h4 className="text-xl font-bold mb-2">{plan.name}</h4>
                                            {plan.description && (
                                                <p
                                                    className={`text-gray-600 mb-4 ${
                                                        plan.id === 'free' ? 'text-base leading-relaxed' : 'text-sm'
                                                    }`}
                                                >
                                                    {plan.description}
                                                </p>
                                            )}
                                            <div className="space-y-2 mb-6">
                                                {plan.prices.map((price) => (
                                                    <div key={price.id}>
                                                        <p className="text-3xl font-bold">
                                                            {price.id === 'free'
                                                                ? 'Free'
                                                                : formatPrice(
                                                                      price.unit_amount || price.amount,
                                                                      price.currency || 'usd'
                                                                  )}
                                                        </p>
                                                        {price.id !== 'free' && (
                                                            <p className="text-gray-600 text-sm">
                                                                / {price.interval || price.recurring?.interval || 'month'}
                                                                {price.intervalCount > 1 && ` (${price.intervalCount})`}
                                                            </p>
                                                        )}
                                                        {(!hasSubscription &&
                                                            price.id === 'free' &&
                                                            currentTier === 'free') && (
                                                            <div className="mt-3 text-center text-primary font-semibold">
                                                                Current Plan
                                                            </div>
                                                        )}
                                                        {(!hasSubscription &&
                                                            price.id === 'free' &&
                                                            currentTier !== 'free') && (
                                                            <button
                                                                onClick={() => handleSubscribe(price.id)}
                                                                disabled={loadingSubscription}
                                                                className="mt-3 w-full bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition disabled:opacity-50"
                                                            >
                                                                {loadingSubscription ? 'Loading...' : 'Downgrade to Free'}
                                                            </button>
                                                        )}
                                                        {(!hasSubscription && price.id !== 'free') && (
                                                            <button
                                                                onClick={() => handleSubscribe(price.id)}
                                                                disabled={loadingSubscription}
                                                                className="mt-3 w-full bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition disabled:opacity-50"
                                                            >
                                                                {loadingSubscription ? 'Loading...' : 'Subscribe'}
                                                            </button>
                                                        )}
                                                        {(hasSubscription &&
                                                            subscription?.priceId &&
                                                            price.id === subscription.priceId) && (
                                                            <div className="mt-3 text-center text-primary font-semibold">
                                                                Current Plan
                                                            </div>
                                                        )}
                                                        {(hasSubscription &&
                                                            subscription?.priceId &&
                                                            price.id !== subscription.priceId &&
                                                            price.id !== 'free') && (
                                                            <button
                                                                onClick={() => handleSubscribe(price.id)}
                                                                disabled={loadingSubscription}
                                                                className="mt-3 w-full bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition disabled:opacity-50"
                                                            >
                                                                {loadingSubscription ? 'Loading...' : 'Switch Plan'}
                                                            </button>
                                                        )}
                                                        {(hasSubscription && price.id === 'free') && (
                                                            <button
                                                                onClick={() => handleSubscribe(price.id)}
                                                                disabled={loadingSubscription}
                                                                className="mt-3 w-full bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 transition disabled:opacity-50"
                                                            >
                                                                {loadingSubscription ? 'Loading...' : 'Downgrade to Free'}
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Invoice History Section */}
            {invoices.length > 0 && showInvoices && (
                <div className="bg-white shadow-md rounded-xl p-6">
                    <h3 className="text-xl font-semibold mb-4">Invoice History</h3>
                    <div className="space-y-2">
                        {invoices.map((invoice) => (
                            <div
                                key={invoice.id}
                                className="flex items-center justify-between p-4 border border-gray-200 rounded hover:bg-gray-50"
                            >
                                <div>
                                    <p className="font-semibold">
                                        {formatPrice(invoice.amount, invoice.currency)} - {invoice.status}
                                    </p>
                                    <p className="text-sm text-gray-600">{formatDate(invoice.created)}</p>
                                </div>
                                {invoice.hostedInvoiceUrl && (
                                <a
                                        href={invoice.hostedInvoiceUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                >
                                        View Invoice
                                </a>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {plans.length === 0 && !loading && (
                <div className="bg-white shadow-md rounded-xl p-6">
                    <p className="text-gray-600 text-center">
                        No subscription plans available. Please contact support.
                    </p>
            </div>
            )}
        </div>
    );
}
